import { Injectable, computed, effect, inject, signal, untracked } from '@angular/core';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { AuthService } from '@app/core/services/Auth/auth-service';
import { environment } from 'src/environments/environment';
import { DEFAULT_AI_HELP_SUGGESTIONS } from '../constants/ai-help-suggestions';
import { AI_HELP_CATEGORY_VISUALS } from '../constants/ai-help-assistant-home-static';
import {
  AiHelpConversationDetailMessage,
  AiHelpConversationItem,
  AiHelpDashboardAttention,
  AiHelpDashboardPopularAction,
  AiHelpDashboardSummary,
  AiHelpDashboardTryAsk,
  AiHelpDashboardTryAskItem,
  AiHelpHistoryEntry,
  AiHelpMessage,
  AiHelpMessageContent,
  AiHelpPromptExampleCategory,
  AiHelpPromptExampleItem,
  AiHelpPromptExampleTone,
  AiHelpPromptExamples
} from '../models/ai-help-assistant.models';
import { AiHelpAssistantApiService } from './ai-help-assistant-api.service';
import { AiHelpSpeechSynthesisService } from './ai-help-speech-synthesis.service';
import {
  normalizeAiHelpResponse,
  parseAiHelpResponse,
  serializeMessageContent,
  serializeMessageContentForHistory
} from '@app/features/utils/ai-help-response.parser';

@Injectable({ providedIn: 'root' })
export class AiHelpAssistantStateService {
  private readonly api = inject(AiHelpAssistantApiService);
  private readonly speechOutput = inject(AiHelpSpeechSynthesisService);
  private readonly auth = inject(AuthService);
  private dashboardSummarySub?: Subscription;
  private dashboardPopularActionsSub?: Subscription;
  private dashboardTryAskSub?: Subscription;
  private promptExamplesSub?: Subscription;
  private conversationsSub?: Subscription;
  private askSub?: Subscription;
  private deleteAllConversationsSub?: Subscription;
  private readonly deleteConversationSubs = new Map<string, Subscription>();
  /** Bumps on logout / user switch / new chat so late API replies cannot leak. */
  private sessionGeneration = 0;
  /** `undefined` until the first auth read; then the bound account id (or null). */
  private boundUserId: string | null | undefined = undefined;

  private readonly _isOpen = signal(false);
  private readonly _messages = signal<AiHelpMessage[]>([]);
  private readonly _isLoading = signal(false);
  private readonly _draft = signal('');
  private readonly _dashboardSummary = signal<AiHelpDashboardSummary | null>(null);
  private readonly _isDashboardSummaryLoading = signal(false);
  private readonly _dashboardPopularActions = signal<AiHelpDashboardPopularAction[] | null>(null);
  private readonly _dashboardTryAsk = signal<AiHelpDashboardTryAsk | null>(null);
  private readonly _promptExamples = signal<AiHelpPromptExamples | null>(null);
  private readonly _isPromptExamplesLoading = signal(false);
  private readonly _promptExamplesError = signal(false);
  private readonly _conversations = signal<AiHelpConversationItem[] | null>(null);
  private readonly _deletingConversationIds = signal<ReadonlySet<string>>(new Set());
  private readonly _isDeletingAllConversations = signal(false);
  private readonly _activeConversationId = signal<string | null>(null);
  private readonly _isHomeView = signal(true);

  constructor() {
    effect(() => {
      const userId = this.currentUserId();
      const previous = untracked(() => this.boundUserId);

      if (previous === undefined) {
        this.boundUserId = userId;
        return;
      }

      if (previous === userId) {
        return;
      }

      this.boundUserId = userId;
      untracked(() => this.resetSession());
    });
  }

  readonly isOpen = this._isOpen.asReadonly();
  readonly messages = this._messages.asReadonly();
  readonly isLoading = this._isLoading.asReadonly();
  readonly draft = this._draft.asReadonly();
  readonly dashboardSummary = this._dashboardSummary.asReadonly();
  readonly isDashboardSummaryLoading = this._isDashboardSummaryLoading.asReadonly();
  readonly dashboardPopularActions = this._dashboardPopularActions.asReadonly();
  readonly dashboardTryAsk = this._dashboardTryAsk.asReadonly();
  readonly isPromptExamplesLoading = this._isPromptExamplesLoading.asReadonly();
  readonly promptExamplesError = this._promptExamplesError.asReadonly();
  readonly conversations = this._conversations.asReadonly();
  readonly deletingConversationIds = this._deletingConversationIds.asReadonly();
  readonly isDeletingAllConversations = this._isDeletingAllConversations.asReadonly();
  readonly activeConversationId = this._activeConversationId.asReadonly();
  readonly isHomeView = this._isHomeView.asReadonly();

  readonly attention = computed((): AiHelpDashboardAttention | null => {
    const summary = this._dashboardSummary();
    const attention = summary?.attention;
    if (!attention) {
      return null;
    }

    const items = (attention.items ?? []).filter(
      (item) => !!item?.id && !!item.prompt?.trim()
    );
    if (!items.length) {
      return null;
    }

    return {
      title: attention.title?.trim() || 'Needs Your Attention',
      ctaLabel: attention.ctaLabel?.trim() || '',
      items
    };
  });

  readonly promptExamples = computed((): AiHelpPromptExamples | null => {
    const payload = this._promptExamples();
    if (!payload) {
      return null;
    }

    const categories = (payload.categories ?? [])
      .map((category) => this.normalizePromptCategory(category))
      .filter((category): category is AiHelpPromptExampleCategory => category != null);

    if (!categories.length) {
      return null;
    }

    const apiTitle = payload.title?.trim() || '';
    const fallbackSubtitle = 'Explore all the things WorkLuge Assistant can help you with.';
    const apiSubtitle = payload.subtitle?.trim() || '';
    return {
      title:
        !apiTitle || /^ai knowledge base$/i.test(apiTitle) ? 'Sample Prompts' : apiTitle,
      subtitle: apiSubtitle
        ? apiSubtitle.replace(/WorkLuge AI\b/gi, 'WorkLuge Assistant')
        : fallbackSubtitle,
      searchPlaceholder: payload.searchPlaceholder?.trim() || 'Search questions...',
      lessLabel: payload.lessLabel?.trim() || 'Show less',
      categories
    };
  });
  readonly suggestions = computed(() => DEFAULT_AI_HELP_SUGGESTIONS);
  readonly hasMessages = computed(() => this._messages().length > 0);
  readonly canSend = computed(() => {
    const text = this._draft().trim();
    return text.length > 0 && !this._isLoading();
  });

  open(): void {
    this._isOpen.set(true);
    this.loadDashboardSummary();
    this.loadDashboardPopularActions();
    this.loadDashboardTryAsk();
    this.loadPromptExamples();
    this.loadConversations();
  }

  close(): void {
    this.speechOutput.stop();
    this.dashboardSummarySub?.unsubscribe();
    this.dashboardPopularActionsSub?.unsubscribe();
    this.dashboardTryAskSub?.unsubscribe();
    this.promptExamplesSub?.unsubscribe();
    this.conversationsSub?.unsubscribe();
    this.deleteAllConversationsSub?.unsubscribe();
    this.deleteAllConversationsSub = undefined;
    for (const sub of this.deleteConversationSubs.values()) {
      sub.unsubscribe();
    }
    this.deleteConversationSubs.clear();
    this._deletingConversationIds.set(new Set());
    this._isDeletingAllConversations.set(false);
    this._isDashboardSummaryLoading.set(false);
    this._isPromptExamplesLoading.set(false);
    this._isOpen.set(false);
  }

  toggle(): void {
    if (this._isOpen()) {
      this.close();
      return;
    }
    this.open();
  }

  /** Retry knowledge-base fetch if the open-prefetch missed or failed. */
  ensurePromptExamples(): void {
    if (this._isPromptExamplesLoading()) {
      return;
    }
    if (this._promptExamples() && !this._promptExamplesError()) {
      return;
    }
    this.loadPromptExamples();
  }

  /** User-triggered refetch from the left panel error state. */
  retryPromptExamples(): void {
    if (this._isPromptExamplesLoading()) {
      return;
    }
    this.loadPromptExamples();
  }

  /** Fresh home summary each time the slider opens. Keeps last payload for instant paint. */
  private loadDashboardSummary(): void {
    this.dashboardSummarySub?.unsubscribe();
    this._isDashboardSummaryLoading.set(true);
    const generation = this.sessionGeneration;

    this.dashboardSummarySub = this.api
      .getDashboardSummary()
      .pipe(
        finalize(() => {
          if (generation === this.sessionGeneration) {
            this._isDashboardSummaryLoading.set(false);
          }
        })
      )
      .subscribe({
        next: (summary) => {
          if (generation !== this.sessionGeneration) {
            return;
          }
          this._dashboardSummary.set(summary);
        },
        error: () => {
          // Keep last successful summary so the home screen does not flicker empty.
        }
      });
  }

  /** Fresh popular actions each time the slider opens. Keeps last payload for instant paint. */
  private loadDashboardPopularActions(): void {
    this.dashboardPopularActionsSub?.unsubscribe();
    const generation = this.sessionGeneration;

    this.dashboardPopularActionsSub = this.api.getDashboardPopularActions().subscribe({
      next: (actions) => {
        if (generation !== this.sessionGeneration) {
          return;
        }
        const normalized = this.normalizeDashboardPopularActions(actions);
        if (normalized.length) {
          this._dashboardPopularActions.set(normalized);
        }
      },
      error: () => {
        // Keep last successful payload so the home screen does not flicker empty.
      }
    });
  }

  /** Fresh "Try asking" suggestions each time the slider opens. Keeps last payload for instant paint. */
  private loadDashboardTryAsk(): void {
    this.dashboardTryAskSub?.unsubscribe();
    const generation = this.sessionGeneration;

    this.dashboardTryAskSub = this.api.getDashboardTryAsk().subscribe({
      next: (payload) => {
        if (generation !== this.sessionGeneration) {
          return;
        }
        const normalized = this.normalizeDashboardTryAsk(payload);
        if (normalized) {
          this._dashboardTryAsk.set(normalized);
        }
      },
      error: () => {
        // Keep last successful payload so the home screen does not flicker empty.
      }
    });
  }

  /** Prefetch knowledge-base prompts so “See more” paints immediately. Keeps last payload. */
  private loadPromptExamples(): void {
    this.promptExamplesSub?.unsubscribe();
    this._isPromptExamplesLoading.set(true);
    this._promptExamplesError.set(false);
    const generation = this.sessionGeneration;

    this.promptExamplesSub = this.api
      .getPromptExamples()
      .pipe(
        finalize(() => {
          if (generation === this.sessionGeneration) {
            this._isPromptExamplesLoading.set(false);
          }
        })
      )
      .subscribe({
        next: (payload) => {
          if (generation !== this.sessionGeneration) {
            return;
          }
          this._promptExamples.set(payload);
          this._promptExamplesError.set(false);
        },
        error: () => {
          if (generation !== this.sessionGeneration) {
            return;
          }
          this._promptExamplesError.set(true);
        }
      });
  }

  /** Fresh question history each time the slider opens. Keeps last payload for instant paint. */
  private loadConversations(): void {
    this.conversationsSub?.unsubscribe();
    const generation = this.sessionGeneration;

    this.conversationsSub = this.api.getConversations().subscribe({
      next: (conversations) => {
        if (generation !== this.sessionGeneration) {
          return;
        }
        this._conversations.set(conversations);
      },
      error: () => {
        // Keep last successful payload so the home screen does not flicker empty.
      }
    });
  }

  setDraft(value: string): void {
    this._draft.set(value);
  }

  startNewChat(): void {
    // Refresh history only when leaving a real thread (messages and/or a backend id).
    const shouldRefreshConversations =
      !!this._activeConversationId()?.trim() || this._messages().length > 0;
    this.clearConversation({ closePanel: false, clearDashboard: false });
    if (shouldRefreshConversations) {
      this.loadConversations();
    }
  }

  /** Return to the home screen without dropping the in-memory conversation. */
  showHome(): void {
    this.speechOutput.stop();
    this._isHomeView.set(true);
  }

   /** Resume the in-memory conversation after visiting home. */
  showThread(): void {
    if (!this.hasMessages()) {
      return;
    }
    this._isHomeView.set(false);
  }
  /**
   * Drop in-memory chat, draft, and home summary so the next account starts clean.
   * Called automatically when the logged-in user id changes (logout, login, impersonation).
   */
  resetSession(): void {
    this.clearConversation({ closePanel: true, clearDashboard: true });
  }

  sendDraft(isAudio = false): void {
    const question = this._draft().trim();
    if (!question || this._isLoading()) {
      return;
    }
    this.sendQuestion(question, isAudio);
  }

  /**
   * @param prompt API question / history content (may be JSON for clarify selections)
   * @param displayContent Optional chat-bubble label when it should differ from `prompt`
   */
  sendSuggestion(prompt: string, displayContent?: string): void {
    if (this._isLoading()) {
      return;
    }
    this.sendQuestion(prompt, false, displayContent);
  }

  /**
   * Open a past conversation via `GET /conversations/{id}` and render its thread.
   */
  loadConversation(conversationId: string): void {
    const id = conversationId?.trim();
    if (!id || this._isLoading()) {
      return;
    }

    this.clearConversation({ closePanel: false, clearDashboard: false });
    this._activeConversationId.set(id);
    this._isHomeView.set(false);
    this._isLoading.set(true);

    const pendingId = `pending-${Date.now()}`;
    this._messages.set([
      {
        id: pendingId,
        role: 'assistant',
        content: '',
        createdAt: Date.now(),
        pending: true
      }
    ]);

    this.askSub?.unsubscribe();
    const generation = this.sessionGeneration;
    this.askSub = this.api
      .getConversationById(id)
      .pipe(
        finalize(() => {
          if (generation === this.sessionGeneration) {
            this._isLoading.set(false);
          }
        })
      )
      .subscribe({
        next: (detail) => {
          if (generation !== this.sessionGeneration) {
            return;
          }
          this._activeConversationId.set(detail.conversationId || id);
          const messages = this.mapConversationDetailToMessages(detail.messages);
          this._messages.set(
            messages.length
              ? messages
              : [
                  this.createMessage(
                    'assistant',
                    'No messages were found for this conversation.',
                    true
                  )
                ]
          );
          this._isHomeView.set(false);
        },
        error: () => {
          if (generation !== this.sessionGeneration) {
            return;
          }
          this._messages.set([
            this.createMessage(
              'assistant',
              'Something went wrong while loading that conversation. Please try again.',
              true
            )
          ]);
          this._isHomeView.set(false);
        }
      });
  }

  /** Whether a history row is mid-delete (disables its delete control). */
  isDeletingConversation(conversationId: string): boolean {
    return this._deletingConversationIds().has(conversationId);
  }

  /**
   * Delete a past conversation via `DELETE /conversation-delete?conversation_id=`.
   * Optimistically removes the list row; restores it if the request fails.
   */
  deleteConversation(conversationId: string): void {
    const id = conversationId?.trim();
    if (!id || this._deletingConversationIds().has(id) || this._isDeletingAllConversations()) {
      return;
    }

    const previous = this._conversations();
    const wasActive = this._activeConversationId()?.trim() === id;

    this._conversations.update((list) =>
      list ? list.filter((item) => item.conversationId !== id) : list
    );

    if (wasActive) {
      this.clearConversation({ closePanel: false, clearDashboard: false });
    }

    this._deletingConversationIds.update((current) => {
      const next = new Set(current);
      next.add(id);
      return next;
    });

    this.deleteConversationSubs.get(id)?.unsubscribe();
    const sub = this.api
      .deleteConversation(id)
      .pipe(
        finalize(() => {
          this.deleteConversationSubs.delete(id);
          this._deletingConversationIds.update((current) => {
            if (!current.has(id)) {
              return current;
            }
            const next = new Set(current);
            next.delete(id);
            return next;
          });
        })
      )
      .subscribe({
        error: () => {
          this._conversations.update((list) => {
            if (list?.some((item) => item.conversationId === id)) {
              return list;
            }
            const restored = previous?.find((item) => item.conversationId === id);
            if (!restored) {
              return list;
            }
            if (!list?.length) {
              return previous?.filter((item) => item.conversationId === id) ?? [restored];
            }

            const insertAt = previous?.findIndex((item) => item.conversationId === id) ?? -1;
            if (insertAt < 0 || insertAt >= list.length) {
              return [...list, restored];
            }
            const next = [...list];
            next.splice(insertAt, 0, restored);
            return next;
          });
        }
      });
    this.deleteConversationSubs.set(id, sub);
  }

  /**
   * Delete every past conversation via `DELETE /conversation-delete?confirm_all=true`.
   * Optimistically clears the list; restores it if the request fails.
   */
  deleteAllConversations(): void {
    if (this._isDeletingAllConversations()) {
      return;
    }

    const previous = this._conversations();
    if (!previous?.length) {
      return;
    }

    const hadActiveThread =
      !!this._activeConversationId()?.trim() || this._messages().length > 0;

    for (const sub of this.deleteConversationSubs.values()) {
      sub.unsubscribe();
    }
    this.deleteConversationSubs.clear();
    this._deletingConversationIds.set(new Set());

    this._conversations.set([]);
    if (hadActiveThread) {
      this.clearConversation({ closePanel: false, clearDashboard: false });
    }

    this._isDeletingAllConversations.set(true);
    this.deleteAllConversationsSub?.unsubscribe();
    this.deleteAllConversationsSub = this.api
      .deleteAllConversations()
      .pipe(
        finalize(() => {
          this.deleteAllConversationsSub = undefined;
          this._isDeletingAllConversations.set(false);
        })
      )
      .subscribe({
        error: () => {
          this._conversations.set(previous);
        }
      });
  }

  private sendQuestion(question: string, isAudio: boolean, displayContent?: string): void {
    this.speechOutput.stop();
    this._isHomeView.set(false);
    const userMessage = this.createMessage('user', displayContent ?? question, false, question);
    const history = this.buildHistory(this._messages());

    this._messages.update((list) => [...list, userMessage]);
    this._draft.set('');

    const pendingId = `pending-${Date.now()}`;
    const pendingMessage: AiHelpMessage = {
      id: pendingId,
      role: 'assistant',
      content: '',
      createdAt: Date.now(),
      pending: true
    };
    this._messages.update((list) => [...list, pendingMessage]);
    this._isLoading.set(true);

    this.askSub?.unsubscribe();
    const generation = this.sessionGeneration;
    const conversationId = this._activeConversationId();
    this.askSub = this.api
      .ask(question, history, isAudio, conversationId)
      .pipe(
        finalize(() => {
          if (generation === this.sessionGeneration) {
            this._isLoading.set(false);
          }
        })
      )
      .subscribe({
        next: (response) => {
          if (generation !== this.sessionGeneration) {
            return;
          }
          const nextConversationId = response.conversationId?.trim();
          if (nextConversationId) {
            this._activeConversationId.set(nextConversationId);
          }
          const message = this.createMessage('assistant', response.content);
          this.replaceMessage(pendingId, message);
          if (message.isAudio && message.messageContent && message.messageContent.kind !== 'clarify') {
            this.speechOutput.speakMessage(message.id, message.messageContent);
          }
        },
        error: () => {
          if (generation !== this.sessionGeneration) {
            return;
          }
          this.replaceMessage(
            pendingId,
            this.createMessage(
              'assistant',
              'Something went wrong while reaching the help assistant. Please try again in a moment.',
              true
            )
          );
        }
      });
  }

  private static readonly PROMPT_TONES: readonly AiHelpPromptExampleTone[] = [
    'purple',
    'green',
    'blue',
    'orange'
  ];

  private normalizePromptCategory(
    category: AiHelpPromptExampleCategory | null | undefined
  ): AiHelpPromptExampleCategory | null {
    if (!category) {
      return null;
    }

    const id = category.id?.trim();
    if (!id) {
      return null;
    }

    const normalizedItems = (category.items ?? [])
      .map((item) => this.normalizePromptItem(item))
      .filter((item): item is AiHelpPromptExampleItem => item != null);

    const seenItemIds = new Set<string>();
    const items = normalizedItems.map((item, index) => {
      if (!seenItemIds.has(item.id)) {
        seenItemIds.add(item.id);
        return item;
      }

      let uniqueId = `${item.id}__${index}`;
      while (seenItemIds.has(uniqueId)) {
        uniqueId = `${item.id}__${index}_${seenItemIds.size}`;
      }
      seenItemIds.add(uniqueId);
      return { ...item, id: uniqueId };
    });

    if (!items.length) {
      return null;
    }

    const rawPreview =
      (category as AiHelpPromptExampleCategory & { preview_count?: unknown }).previewCount ??
      (category as { preview_count?: unknown }).preview_count;
    const previewCount = this.toPositiveInt(rawPreview, items.length);
    const remaining = Math.max(0, items.length - previewCount);
    const visual = AI_HELP_CATEGORY_VISUALS[id];

    return {
      id,
      title: category.title?.trim() || 'Questions',
      description: category.description?.trim() || '',
      icon: visual?.icon || category.icon?.trim() || 'help_outline',
      tone: visual?.tone || this.normalizePromptTone(category.tone),
      previewCount,
      moreCount: this.toNonNegativeInt(category.moreCount, remaining),
      items
    };
  }

  private normalizePromptItem(
    item: AiHelpPromptExampleItem | null | undefined
  ): AiHelpPromptExampleItem | null {
    if (!item) {
      return null;
    }

    const id = item.id?.trim();
    const prompt = item.prompt?.trim();
    if (!id || !prompt) {
      return null;
    }

    return {
      id,
      label: item.label?.trim() || prompt,
      prompt
    };
  }

  private normalizePromptTone(tone: string | undefined): AiHelpPromptExampleTone {
    const value = tone?.trim().toLowerCase();
    return AiHelpAssistantStateService.PROMPT_TONES.includes(value as AiHelpPromptExampleTone)
      ? (value as AiHelpPromptExampleTone)
      : 'purple';
  }

  private normalizeDashboardPopularActions(
    actions: AiHelpDashboardPopularAction[] | null | undefined
  ): AiHelpDashboardPopularAction[] {
    return (actions ?? [])
      .map((action) => this.normalizeDashboardHomePromptItem(action))
      .filter((action): action is AiHelpDashboardPopularAction => action != null);
  }

  private normalizeDashboardTryAsk(
    payload: AiHelpDashboardTryAsk | null | undefined
  ): AiHelpDashboardTryAsk | null {
    if (!payload) {
      return null;
    }

    const items = (payload.items ?? [])
      .map((item) => this.normalizeDashboardHomePromptItem(item))
      .filter((item): item is AiHelpDashboardTryAskItem => item != null);

    if (!items.length) {
      return null;
    }

    return {
      title: payload.title?.trim() || 'Try asking WorkLuge Assistant',
      ctaLabel: payload.ctaLabel?.trim() || 'See more',
      items,
      restricted: payload.restricted
    };
  }

  private normalizeDashboardHomePromptItem<
    T extends {
      id: string;
      label: string;
      icon: string;
      prompt: string;
      tone?: 'blue' | 'green' | 'red' | 'orange' | 'teal' | 'indigo';
    }
  >(item: T | null | undefined): T | null {
    if (!item) {
      return null;
    }

    const id = item.id?.trim();
    const prompt = item.prompt?.trim();
    if (!id || !prompt) {
      return null;
    }

    return {
      ...item,
      id,
      label: item.label?.trim() || prompt,
      icon: item.icon?.trim() || 'help_outline',
      prompt,
      tone: this.normalizeDashboardHomePromptTone(item.tone)
    };
  }

  private normalizeDashboardHomePromptTone(
    tone: string | undefined
  ): 'blue' | 'green' | 'red' | 'orange' | 'teal' | 'indigo' {
    switch (tone?.trim().toLowerCase()) {
      case 'green':
      case 'red':
      case 'orange':
      case 'teal':
      case 'indigo':
        return tone.trim().toLowerCase() as 'green' | 'red' | 'orange' | 'teal' | 'indigo';
      case 'blue':
      default:
        return 'blue';
    }
  }

  private toPositiveInt(value: unknown, fallback: number): number {
    const parsed = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(parsed) || parsed < 1) {
      return fallback;
    }
    return Math.floor(parsed);
  }

  private toNonNegativeInt(value: unknown, fallback: number): number {
    const parsed = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(parsed) || parsed < 0) {
      return fallback;
    }
    return Math.floor(parsed);
  }

  private currentUserId(): string | null {
    const user = this.auth.userData();
    const id = user?._id ?? user?.id ?? null;
    return id != null && String(id).trim() ? String(id) : null;
  }

  private clearConversation(options: { closePanel: boolean; clearDashboard: boolean }): void {
    this.sessionGeneration += 1;
    this.askSub?.unsubscribe();
    this.askSub = undefined;
    this.speechOutput.stop();
    this._messages.set([]);
    this._draft.set('');
    this._isLoading.set(false);
    this._activeConversationId.set(null);
    this._isHomeView.set(true);

    if (options.closePanel) {
      this.dashboardSummarySub?.unsubscribe();
      this.dashboardSummarySub = undefined;
      this.dashboardPopularActionsSub?.unsubscribe();
      this.dashboardPopularActionsSub = undefined;
      this.dashboardTryAskSub?.unsubscribe();
      this.dashboardTryAskSub = undefined;
      this.promptExamplesSub?.unsubscribe();
      this.promptExamplesSub = undefined;
      this.conversationsSub?.unsubscribe();
      this.conversationsSub = undefined;
      this.deleteAllConversationsSub?.unsubscribe();
      this.deleteAllConversationsSub = undefined;
      for (const sub of this.deleteConversationSubs.values()) {
        sub.unsubscribe();
      }
      this.deleteConversationSubs.clear();
      this._deletingConversationIds.set(new Set());
      this._isDeletingAllConversations.set(false);
      this._isOpen.set(false);
    }

    if (options.clearDashboard) {
      this._dashboardSummary.set(null);
      this._isDashboardSummaryLoading.set(false);
      this._dashboardPopularActions.set(null);
      this._dashboardTryAsk.set(null);
      this._promptExamples.set(null);
      this._isPromptExamplesLoading.set(false);
      this._promptExamplesError.set(false);
      this._conversations.set(null);
    }
  }

  private buildHistory(messages: AiHelpMessage[]): AiHelpHistoryEntry[] {
    return messages
      .filter((message) => !message.pending && this.toHistoryContent(message).trim())
      .map((message) => ({
        role: message.role,
        content: this.toHistoryContent(message).trim()
      }));
  }

  private mapConversationDetailToMessages(
    turns: AiHelpConversationDetailMessage[]
  ): AiHelpMessage[] {
    const mediaBaseUrl = environment.helpdeskApiUrl;
    return turns.map((turn, index) => {
      const createdAt = this.parseConversationTimestamp(turn.createdAt) ?? Date.now() + index;

      if (turn.role === 'user') {
        const message = this.createMessage('user', turn.content);
        return { ...message, createdAt };
      }

      if (turn.raw != null) {
        const parsed = normalizeAiHelpResponse(parseAiHelpResponse(turn.raw), mediaBaseUrl);
        const message = this.createMessage('assistant', parsed.content);
        return { ...message, createdAt };
      }

      const message = this.createMessage('assistant', turn.content || '…');
      return { ...message, createdAt };
    });
  }

  private parseConversationTimestamp(value: string | undefined): number | null {
    const raw = value?.trim();
    if (!raw) {
      return null;
    }
    const parsed = Date.parse(raw);
    return Number.isFinite(parsed) ? parsed : null;
  }

  /** Prefer API-shaped JSON for structured assistant turns and clarify user picks. */
  private toHistoryContent(message: AiHelpMessage): string {
    if (message.role === 'assistant' && message.messageContent) {
      return serializeMessageContentForHistory(message.messageContent);
    }
    return (message.apiContent ?? message.content).trim();
  }

  private createMessage(
    role: 'user' | 'assistant',
    content: string | AiHelpMessageContent,
    error = false,
    apiContent?: string
  ): AiHelpMessage {
    const messageContent = typeof content === 'string' ? undefined : content;
    const text = typeof content === 'string' ? content : serializeMessageContent(content);
    const isAudio = typeof content === 'string' ? false : content.isAudio === true;

    return {
      id: `${role}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      role,
      content: text,
      apiContent: apiContent && apiContent !== text ? apiContent : undefined,
      messageContent,
      createdAt: Date.now(),
      error,
      isAudio
    };
  }

  private replaceMessage(id: string, next: AiHelpMessage): void {
    this._messages.update((list) => list.map((message) => (message.id === id ? next : message)));
  }
}
